import React, { useEffect, useState } from "react";
import axios from "../../api/axios";

// MUI components
import {
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Icon from "@mui/material/Icon";

// Custom components
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function StaffCategories() {
  const [categories, setCategories] = useState([]);
  const [grades, setGrades] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    name: "",
    grade_level_id: "",
    salary_structure_id: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch all staff categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get("/staff-categories");
      setCategories(response.data);
    } catch (err) {
      console.error("Failed to fetch Staff Categories", err);
    }
  };

  // Fetch Grade Levels
  const fetchGrades = async () => {
    try {
      const response = await axios.get("/listgrade");
      setGrades(response.data);
    } catch (err) {
      console.error("Failed to fetch grade levels", err);
    }
  };

  // Fetch Salary Structures
  const fetchSalaryStructures = async () => {
    try {
      const response = await axios.get("/listsalarystructure");
      setSalaryStructures(response.data);
    } catch (err) {
      console.error("Failed to fetch salary structures", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchGrades();
    fetchSalaryStructures();
  }, []);

  // Save or Update category
  const handleSave = async () => {
    try {
      const payload = {
        name: currentItem.name,
        grade_level_id: currentItem.grade_level_id,
        salary_structure_id: currentItem.salary_structure_id,
      };

      if (currentItem.id) {
        await axios.put(`/staff-categories/${currentItem.id}`, payload);
        setSnackbar({ open: true, message: "Staff Category updated successfully" });
      } else {
        await axios.post("/staff-categories", payload);
        setSnackbar({ open: true, message: "Staff Category added successfully" });
      }

      fetchCategories();
      setOpenDialog(false);
      setCurrentItem({ id: null, name: "", grade_level_id: "", salary_structure_id: "" });
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving.");
    }
  };

  // Edit record
  const handleEdit = (item) => {
    setCurrentItem(item);
    setOpenDialog(true);
  };

  // Delete record
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Staff Category?")) {
      try {
        await axios.delete(`/staff-categories/${id}`);
        fetchCategories();
        setSnackbar({ open: true, message: "Staff Category deleted successfully" });
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginate data
  const paginatedItems = categories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container justifyContent="space-between" alignItems="center" px={2} mb={2}>
          <Typography variant="h5">Staff Categories</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDialog(true)}
            sx={{ color: "#fff" }}
          >
            Add New
          </Button>
        </Grid>

        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
          <Table>
            <TableBody>
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {grades.find((g) => g.id === item.grade_level_id)?.name ||
                        item.grade_level_id}
                    </TableCell>
                    <TableCell>
                      {salaryStructures.find((s) => s.id === item.salary_structure_id)?.name ||
                        item.salary_structure_id}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton color="primary" onClick={() => handleEdit(item)}>
                        <Icon>edit</Icon>
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item.id)}>
                        <Icon>delete</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ padding: 3 }}>
                    No Staff Categories found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={categories.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>
          {currentItem.id ? "Edit Staff Category" : "Add New Staff Category"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            fullWidth
            margin="normal"
            value={currentItem.name}
            onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Grade Level</InputLabel>
            <Select
              value={currentItem.grade_level_id}
              label="Grade Level"
              onChange={(e) => setCurrentItem({ ...currentItem, grade_level_id: e.target.value })}
            >
              {grades.map((grade) => (
                <MenuItem key={grade.id} value={grade.id}>
                  {grade.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Salary Structure</InputLabel>
            <Select
              value={currentItem.salary_structure_id}
              label="Salary Structure"
              onChange={(e) =>
                setCurrentItem({ ...currentItem, salary_structure_id: e.target.value })
              }
            >
              {salaryStructures.map((structure) => (
                <MenuItem key={structure.id} value={structure.id}>
                  {structure.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default StaffCategories;
